import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import { getOperatorsForGroupType } from '../config/groupFilterProperties';
import { getDataTypeIcon } from './UserPropertyFilterModule';
import axiosInstance from '../../../shared/services/api/axiosInstance';

const OperatorDropdown = ({ value, operators, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const safeOperators = Array.isArray(operators) && operators.length > 0 ? operators : [{ value: 'is', label: 'Is' }];
  const selectedOperator = safeOperators.find((op) => op.value === value) || safeOperators[0];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[100px]"
      >
        {selectedOperator.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${position.width || 100}px`,
                minWidth: '100px',
              }}
            >
              <div className="py-1">
                {safeOperators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => { onChange(op.value); setIsOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${value === op.value ? 'bg-gray-200 text-[#0f172a]' : ''}`}
                  >
                    <span>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

const PropertyMultiselectInput = ({ value = [], options = [], onChange, placeholder = 'Select values...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(
    (opt) =>
      opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    onChange(newValue);
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[140px] text-left flex items-center justify-between gap-1"
      >
        <span className={value.length === 0 ? 'text-[#475569]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#475569] flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${Math.max(position.width || 200, 200)}px`,
                minWidth: '200px',
              }}
            >
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="py-1">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[#475569]">No options found</div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleOption(option.value)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                          isSelected
                            ? 'bg-[#4b57aa] text-white hover:bg-[#4b57aa]'
                            : 'text-[#0f172a] hover:bg-gray-50'
                        }`}
                      >
                        <input type="checkbox" checked={isSelected} readOnly className="rounded" />
                        <span>{option.label || option.value}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

const STANDARD_GROUP_FIELDS = ['group_id', 'member_count', 'last_updated_at'];

/**
 * Filter chip for Groups page: WHERE → group property → operator → value.
 */
const GroupFilterChip = ({
  filter,
  isFirst,
  onEdit,
  onRemove,
  onUpdate,
  teamId,
  groupKey,
}) => {
  const connector = filter.connector || (isFirst ? 'WHERE' : 'AND');
  const type = filter.propertyType || 'string';
  const DataTypeIcon = getDataTypeIcon(type);
  const operators = type === 'list' ? getOperatorsForGroupType(filter.listItemType || 'string') : getOperatorsForGroupType(type);
  const defaultOp = operators[0]?.value || 'is';
  const currentOperator = filter.operator || defaultOp;
  const listMode = filter.listMode || 'any_in_list';

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingPropertyOptions, setLoadingPropertyOptions] = useState(false);

  const propKey = filter.property || filter.field || '';
  const isStandardField = STANDARD_GROUP_FIELDS.includes(propKey);

  // Effective options: prefer filter.sampleValues (custom props); else use API-fetched propertyOptions
  const optionsFromSampleValues = (filter.sampleValues && Array.isArray(filter.sampleValues))
    ? filter.sampleValues.map((v) => (typeof v === 'object' && v !== null && 'value' in v ? { value: v.value, label: v.label ?? v.value } : { value: String(v), label: String(v) }))
    : [];
  const hasSampleValues = optionsFromSampleValues.length > 0;
  const effectivePropertyOptions = hasSampleValues ? optionsFromSampleValues : propertyOptions;

  useEffect(() => {
    if (hasSampleValues) return;

    const needsDropdown =
      (type === 'string' && (currentOperator === 'is' || currentOperator === 'is_not')) ||
      (type === 'list' &&
        (currentOperator === 'is' ||
          currentOperator === 'is_not' ||
          currentOperator === 'contains' ||
          currentOperator === 'does_not_contain'));

    if (isStandardField || !propKey || !teamId || !groupKey || !needsDropdown) {
      setPropertyOptions([]);
      setLoadingPropertyOptions(false);
      return;
    }

    let cancelled = false;
    setLoadingPropertyOptions(true);
    setPropertyOptions([]);
    axiosInstance
      .get('/group-identities/property-values', {
        params: { team_id: teamId, group_key: groupKey, property_key: propKey },
      })
      .then((r) => {
        if (cancelled) return;
        const vals = r.data?.values || [];
        setPropertyOptions(vals.map((v) => ({ value: String(v), label: String(v) })));
      })
      .catch(() => {
        if (!cancelled) setPropertyOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPropertyOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [type, currentOperator, propKey, teamId, groupKey, isStandardField, hasSampleValues]);

  const renderValueInput = () => {
    if (type === 'string') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      if (currentOperator === 'is' || currentOperator === 'is_not') {
        if (loadingPropertyOptions) {
          return <span className="px-2 py-1 text-xs text-[#64748b]">Loading values…</span>;
        }
        if (effectivePropertyOptions.length > 0) {
          return (
            <PropertyMultiselectInput
              value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
              options={effectivePropertyOptions}
              onChange={(newValue) => onUpdate({ value: newValue })}
              placeholder="Select values..."
            />
          );
        }
      }
      return (
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value || null })}
          placeholder="Enter value..."
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'list') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      if (currentOperator === 'is' || currentOperator === 'is_not' || currentOperator === 'contains' || currentOperator === 'does_not_contain') {
        if (loadingPropertyOptions) {
          return <span className="px-2 py-1 text-xs text-[#64748b]">Loading values…</span>;
        }
        if (effectivePropertyOptions.length > 0) {
          return (
            <PropertyMultiselectInput
              value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
              options={effectivePropertyOptions}
              onChange={(newValue) => onUpdate({ value: newValue })}
              placeholder="Select values..."
            />
          );
        }
      }
      return (
        <input
          type="text"
          value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '')}
          onChange={(e) => onUpdate({ value: e.target.value.trim() ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : null })}
          placeholder="Comma-separated values..."
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors min-w-[120px]"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'number') {
      if (currentOperator === 'is_numeric' || currentOperator === 'is_not_numeric') return null;
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="From..."
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569]">to</span>
            <input
              type="number"
              value={filter.value2 ?? ''}
              onChange={(e) => onUpdate({ value2: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="To..."
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      return (
        <input
          type="number"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
          placeholder="Value"
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-28"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'boolean') return null;

    // Object type: show nested properties inline in same view with data type (like Activity > Events)
    if (type === 'object') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      const nestedProps = filter.nestedProperties || [];
      if (nestedProps.length > 0) {
        const baseKey = (filter.property || '').replace(/^custom\./, '');
        return (
          <div className="flex items-center gap-1 flex-wrap">
            {/* Nested properties shown inline with data type - all in same view */}
            <div className="flex flex-wrap gap-1">
              {nestedProps.map((np) => {
                const NestedIcon = getDataTypeIcon(np.type?.startsWith('list') ? 'list' : np.type || 'string');
                const isSelected = filter.nestedPropertyKey === np.name;
                return (
                  <button
                    key={np.name}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const nestedType = np.type || 'string';
                      let listItemType = null;
                      if (nestedType.startsWith('list<') && nestedType.endsWith('>')) {
                        listItemType = nestedType.slice(5, -1);
                      }
                      const fullPath = baseKey ? `custom.${baseKey}.${np.name}` : `custom.${np.name}`;
                      onUpdate({
                        property: fullPath,
                        propertyLabel: baseKey ? `${baseKey}.${np.name}` : np.name,
                        propertyType: nestedType.startsWith('list') ? 'list' : nestedType,
                        listItemType: listItemType || undefined,
                        nestedPropertyKey: np.name,
                        sampleValues: np.sampleValues || [],
                        listMode: nestedType.startsWith('list') ? 'any_in_list' : undefined,
                        value: null,
                      });
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                      isSelected ? 'bg-[#4b57aa] text-white' : 'bg-gray-100 text-[#0f172a] hover:bg-gray-200'
                    }`}
                  >
                    <NestedIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>{np.name}</span>
                    <span className="text-[10px] opacity-80">{np.type || 'string'}</span>
                  </button>
                );
              })}
            </div>
            {filter.nestedPropertyKey && (() => {
              const nested = nestedProps.find((n) => n.name === filter.nestedPropertyKey);
              const opts = (nested?.sampleValues || []).map((v) =>
                typeof v === 'object' && v?.value != null ? { value: v.value, label: v.label ?? v.value } : { value: String(v), label: String(v) }
              );
              if (opts.length > 0 && (currentOperator === 'is' || currentOperator === 'is_not')) {
                return (
                  <PropertyMultiselectInput
                    value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
                    options={opts}
                    onChange={(v) => onUpdate({ value: v })}
                    placeholder="Select value..."
                  />
                );
              }
              return (
                <input
                  type="text"
                  value={filter.value ?? ''}
                  onChange={(e) => onUpdate({ value: e.target.value || null })}
                  placeholder="Enter value..."
                  className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 w-24"
                  onClick={(e) => e.stopPropagation()}
                />
              );
            })()}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value || null })}
          placeholder="Path (e.g. key.nested)"
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 min-w-[120px]"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'datetime' || type === 'date') {
      if (currentOperator === 'last' || currentOperator === 'not_in_the_last') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
              placeholder="X"
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <select
              value={filter.value2 || 'hours'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            {currentOperator === 'not_in_the_last' && <span className="text-xs text-[#475569]">ago</span>}
          </div>
        );
      }
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        // Stack the date range vertically and cap width so it always fits inside the sidebar
        return (
          <div className="flex flex-col gap-1 w-full max-w-[190px]">
            <input
              type="datetime-local"
              value={filter.value ? new Date(filter.value).toISOString().slice(0, 16) : ''}
              onChange={(e) => onUpdate({ value: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="w-full max-w-[190px] px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569] text-center">to</span>
            <input
              type="datetime-local"
              value={filter.value2 ? new Date(filter.value2).toISOString().slice(0, 16) : ''}
              onChange={(e) => onUpdate({ value2: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="w-full max-w-[190px] px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      if (['on', 'not_on', 'before', 'since', 'in_the_next', 'before_the_last'].includes(currentOperator)) {
        if (currentOperator === 'before_the_last') {
          return (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={filter.value ?? ''}
                onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
                placeholder="X"
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-16"
                onClick={(e) => e.stopPropagation()}
              />
              <select
                value={filter.value2 || 'hours'}
                onChange={(e) => onUpdate({ value2: e.target.value })}
                className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
                <option value="weeks">weeks</option>
                <option value="months">months</option>
                <option value="years">years</option>
              </select>
              <span className="text-xs text-[#475569]">ago</span>
            </div>
          );
        }
        return (
          <input
            type="datetime-local"
            value={filter.value ? new Date(filter.value).toISOString().slice(0, 16) : ''}
            onChange={(e) => onUpdate({ value: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
    }

    return (
      <input
        type="text"
        value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '')}
        onChange={(e) => onUpdate({ value: e.target.value.trim() || null })}
        placeholder="Value"
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors min-w-[120px]"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isFirst ? (
        <span className="text-xs font-medium text-[#475569] px-2">WHERE</span>
      ) : (
        <OperatorDropdown
          value={connector}
          operators={[{ value: 'AND', label: 'AND' }, { value: 'OR', label: 'OR' }]}
          onChange={(newConnector) => onUpdate({ connector: newConnector })}
        />
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
      >
        <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
        <span
          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
          title="Group property"
        >
          G
        </span>
        <span className="text-sm text-[#0f172a] font-medium">{filter.propertyLabel}</span>
      </div>
      {type === 'list' && (
        <OperatorDropdown
          value={listMode}
          operators={[{ value: 'any_in_list', label: 'Any in list' }, { value: 'all_in_list', label: 'All in list' }]}
          onChange={(newListMode) => onUpdate({ listMode: newListMode, value: null })}
        />
      )}
      <OperatorDropdown
        value={currentOperator}
        operators={operators}
        onChange={(newOperator) => {
          const updates = { operator: newOperator, value2: null };
          if (type === 'boolean') {
            updates.value = newOperator === 'is_true';
          } else {
            updates.value = null;
          }
          onUpdate(updates);
        }}
      />
      {renderValueInput()}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1.5 text-[#475569] hover:text-[#475569] transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

export default GroupFilterChip;
